import React from "react";
import {
  convert24To12String,
  removeLeadingZeros,
} from "../../../utils/datetime";

/**
 * Resolves a job id to its job number for tooltip display.
 *
 * @param {number|string} jobId Job id or fallback value.
 * @param {Array<Object>} jobsList Jobs list from CoreContext.
 * @returns {number|string} Job number, or `jobId` if not found.
 */
const getJobNumber = (jobId, jobsList) => {
  if (!jobsList || !jobId) return jobId;
  const job = jobsList.find((j) => j.id === jobId);
  return job ? job.number : jobId;
};

/**
 * Returns true only if both values are defined and different.
 *
 * @param {*} value1 First compared value.
 * @param {*} value2 Second compared value.
 * @returns {boolean} Whether the pair is a conflict.
 */
const isConflictingValue = (value1, value2) => {
  // If either value is undefined/null, no conflict
  if (value1 == null || value2 == null) return false;
  // Only conflict if both are defined and different
  return value1 !== value2;
};

/**
 * Returns true if any pair among three values conflicts.
 *
 * @param {*} value1 First value.
 * @param {*} value2 Second value.
 * @param {*} value3 Third value.
 * @returns {boolean} Whether any pair conflicts.
 */
const hasAnyConflict = (value1, value2, value3) => {
  return (
    isConflictingValue(value1, value2) ||
    isConflictingValue(value1, value3) ||
    isConflictingValue(value2, value3)
  );
};

/**
 * Formats a tooltip cell: dash for empty, 12-hour time, or job number.
 *
 * @param {*} value Raw stored value.
 * @param {boolean} [isTime=false] Whether to format as 12-hour time.
 * @param {boolean} [isJobConflict=false] Whether to resolve a job id.
 * @param {Array<Object>|null} [jobsList=null] Jobs list for job numbers.
 * @returns {string|*} Display value.
 */
const formatDisplayValue = (
  value,
  isTime = false,
  isJobConflict = false,
  jobsList = null
) => {
  if (value == null || value === "") {
    return "-";
  }
  if (isTime) {
    return convert24To12String(removeLeadingZeros(value));
  }
  return isJobConflict ? getJobNumber(value, jobsList) : value;
};

/**
 * ConflictTooltip for worker vs manager vs payout time or job values.
 * `onTimeSelect` is a legacy prop; prefer `onValueSelect`.
 *
 * @param {Object} props Component props.
 * @param {Object} props.shift Shift row with worker/hours-log/payout fields.
 * @param {string} [props.timeType] `'start'` or `'finish'` for time conflicts.
 * @param {string} [props.fieldType] `'time'` or `'job'`.
 * @param {boolean} props.isEditing Whether values are clickable.
 * @param {number} props.dayIndex Day index for fallback DOM ids.
 * @param {number} props.shiftIndex Shift index for fallback DOM ids.
 * @param {Function} [props.onTimeSelect] Legacy time-selection callback.
 * @param {Function} [props.onValueSelect] Preferred value-selection callback.
 * @param {Array<Object>} [props.jobsList] Jobs list for job-number display.
 * @param {boolean} [props.isReopenedReview=false] Reopened payout-review layout.
 * @param {string} [props.className] Extra wrapper classes.
 * @returns {JSX.Element} Conflict indicator and hover tooltip.
 */
const ConflictTooltip = ({
  shift,
  timeType, // 'start' or 'finish' (for time conflicts)
  fieldType, // 'time' or 'job' (for generic conflicts)
  isEditing,
  dayIndex,
  shiftIndex,
  onTimeSelect, // Legacy prop for time selection
  onValueSelect, // Generic prop for value selection
  jobsList, // Required for job conflicts
  isReopenedReview = false,
  className = "",
}) => {
  // Determine if this is a time or job conflict
  const isTimeConflict = timeType || fieldType === "time";
  const isJobConflict = fieldType === "job";

  // Get values for time conflicts
  const workerTime = timeType === "start" ? shift.startTime : shift.finishTime;
  const managerTime =
    timeType === "start" ? shift.startTimeHoursLog : shift.finishTimeHoursLog;
  const payoutTime =
    timeType === "start" ? shift.startTimePayout : shift.finishTimePayout;

  // Get values for job conflicts
  const workerJob = shift.job;
  const managerJob = shift.jobHoursLog;
  const payoutJob = shift.jobPayout;

  // Get the relevant values based on conflict type
  const managerValue = isJobConflict ? managerJob : managerTime;
  const workerValue = isJobConflict ? workerJob : workerTime;
  const payoutValue = isJobConflict ? payoutJob : payoutTime;

  // Determine if there is a conflict (only when defined values differ)
  const hasJobConflict =
    isJobConflict && hasAnyConflict(workerJob, managerJob, payoutJob);
  const hasTimeConflict =
    isTimeConflict && hasAnyConflict(workerTime, managerTime, payoutTime);
  const hasConflict = hasJobConflict || hasTimeConflict;

  const tooltipClasses =
    "absolute z-10 shadow-lg text-base px-2 py-1 rounded text-xs whitespace-nowrap bottom-12 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-100 pointer-events-auto hover:opacity-100 hover:visible bg-gray-100";

  // Arrow classes
  const arrowClasses = `absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-transparent border-l-8 border-r-8 border-t-8 border-t-gray-100`;

  /**
   * Applies a selected worker/manager/payout value via callback or DOM fallback.
   *
   * @param {*} selectedValue Chosen conflict value.
   * @returns {void}
   */
  const handleValueSelection = (selectedValue) => {
    if (selectedValue == null || selectedValue === "") {
      return;
    }

    if (onValueSelect) {
      onValueSelect(selectedValue);
    } else if (onTimeSelect && isTimeConflict) {
      // Backward compatibility for time selection
      onTimeSelect(selectedValue);
    } else {
      // Fallback to DOM manipulation for non-editing mode
      const elementId = isJobConflict
        ? `job-${dayIndex}-${shiftIndex}`
        : `${timeType}-${dayIndex}-${shiftIndex}`;
      const element = document.getElementById(elementId);
      if (element) {
        element.textContent = formatDisplayValue(
          selectedValue,
          isTimeConflict,
          isJobConflict,
          jobsList
        );
        element.parentElement.className =
          element.parentElement.className.replace("text-red-600", "");
      }
    }
  };

  // Build tooltip content based on conflict type and editing mode
  let tooltipContent;
  const hasTooltipValue = [workerValue, managerValue, payoutValue].some(
    (value) => value != null && value !== ""
  );

  /**
   * Renders one labeled value row, clickable when editing.
   *
   * @param {string} label Row label.
   * @param {*} value Raw value.
   * @returns {JSX.Element} Tooltip row.
   */
  const renderValueRow = (label, value) => {
    const formattedValue = formatDisplayValue(
      value,
      isTimeConflict,
      isJobConflict,
      jobsList
    );
    const canSelect = isEditing && value != null && value !== "";

    return (
      <div>
        {label}:{" "}
        {canSelect ? (
          <span
            className="cursor-pointer hover:underline font-semibold text-blue-600"
            onClick={() => handleValueSelection(value)}
          >
            {formattedValue}
          </span>
        ) : (
          <span className="font-semibold">{formattedValue}</span>
        )}
      </div>
    );
  };

  if (isReopenedReview && hasTooltipValue) {
    tooltipContent = (
      <>
        <div>Saved data loaded (Click to Set):</div>
        {renderValueRow("Worker", workerValue)}
        {renderValueRow("Manager", managerValue)}
        {renderValueRow("Payout", payoutValue)}
      </>
    );
  }

  // If no conflict, show match message
  else if (!hasConflict && managerValue && isEditing) {
    tooltipContent = (
      <>
        <div>Manager/Worker {isJobConflict ? "Jobs" : "Hours"} Punch Match</div>
      </>
    );
  }
  // For payout conflicts
  else if (!isEditing && payoutValue !== workerValue) {
    tooltipContent = (
      <>
        {!isConflictingValue(managerValue, workerValue) && managerValue ? (
          <>
            <div>
              {isJobConflict
                ? "Worker & Manager Job:"
                : "Worker & Manager Punch:"}
            </div>
            <div className="font-semibold">
              {formatDisplayValue(
                workerValue,
                isTimeConflict,
                isJobConflict,
                jobsList
              )}
            </div>
          </>
        ) : (
          <>
            <div>{isJobConflict ? "Worker Job:" : "Worker Punch:"}</div>
            <div className="font-semibold">
              {formatDisplayValue(
                workerValue,
                isTimeConflict,
                isJobConflict,
                jobsList
              )}
            </div>
            {managerValue && isConflictingValue(managerValue, payoutValue) && (
              <>
                <div>{isJobConflict ? "Manager Job:" : "Manager Punch:"}</div>
                <div className="font-semibold">
                  {formatDisplayValue(
                    managerValue,
                    isTimeConflict,
                    isJobConflict,
                    jobsList
                  )}
                </div>
              </>
            )}
          </>
        )}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"></div>
      </>
    );
  }
  // For editing mode
  else if (isEditing && hasConflict) {
    tooltipContent = (
      <>
        {!isConflictingValue(managerValue, workerValue) ? (
          <>
            <div>
              {isJobConflict
                ? "Job Payout Overridden (Click to Select):"
                : "Time Payout Overridden (Click to Select):"}
            </div>
            <div>
              Worker {managerValue && "& Manager"}{" "}
              {isJobConflict ? "Job:" : "Punch:"}{" "}
              <span
                className="cursor-pointer hover:underline font-semibold text-blue-600"
                onClick={() => handleValueSelection(workerValue)}
              >
                {formatDisplayValue(
                  workerValue,
                  isTimeConflict,
                  isJobConflict,
                  jobsList
                )}
              </span>
            </div>
          </>
        ) : (
          <>
            <div>
              {isJobConflict
                ? "Job Conflict Detected (Click to Select):"
                : "Time Conflict Detected (Click to Select):"}
            </div>
            <div>
              Worker:{" "}
              <span
                className="cursor-pointer hover:underline font-semibold text-blue-600"
                onClick={() => handleValueSelection(workerValue)}
              >
                {formatDisplayValue(
                  workerValue,
                  isTimeConflict,
                  isJobConflict,
                  jobsList
                )}
              </span>
            </div>
            {managerValue && (
              <div>
                Manager:{" "}
                <span
                  className="cursor-pointer hover:underline font-semibold text-blue-600"
                  onClick={() => handleValueSelection(managerValue)}
                >
                  {formatDisplayValue(
                    managerValue,
                    isTimeConflict,
                    isJobConflict,
                    jobsList
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  const additonalMessages =
    shift.missingLogs && isEditing ? (
      <>
        <div className="">Manager daily log missing</div>
        {!tooltipContent && <div className={arrowClasses}></div>}
      </>
    ) : null;

  // Render tooltip with optional missing logs message
  return additonalMessages || tooltipContent ? (
    <div className={tooltipClasses}>
      {additonalMessages}
      {tooltipContent}
      <div className={arrowClasses}></div>
    </div>
  ) : null;
};

export default ConflictTooltip;
